"use strict";

import ItemContentView from "/js/interaction-views/item-content-view.js";
import AppUtil from "/js/util/app-util";
import Strings from "/js/util/strings.js";
import Constants from '/js/core/constants.js';
import FocusUtil from '/js/util/focus-util.js';

/**
 * A single step in the questionnaire interaction with one or two text entry fields.
 */
export default class QuestionnaireTextEntryItemContentView extends ItemContentView {
  
  #$textInputs;  // jquery array of textinput elements
  #$nextButton;

  #charLength = 50;  // a reasonable default value.
  #isInt;
  #isFloat;
  #range;

  constructor(questionObject, index, numItems, answerObject=null) {
    super(questionObject, index, numItems, answerObject);

    if (questionObject.textSpecs) {
      const specs = questionObject.textSpecs;
      if (!isNaN(parseInt(specs.charLength))) {
        this.#charLength = parseInt(specs.charLength)
      } 
      this.#isInt = (specs.dataType === "int");
      this.#isFloat = (specs.dataType === "float");
      if (this.#isInt || this.#isFloat) {
        let min = parseFloat(specs.min);
        let max = parseFloat(specs.max);
        if (!isNaN(min) && !isNaN(max)) {
          this.#range = [min, max];
        }
      }
    }

    // Make text inputs.
    let inputsString = "";
    for (let i = 0; i < questionObject.contentkeys.length; i++) {
      // `contentkeys` and `placeholders` are parallel arrays:
      const contentKey = questionObject.contentkeys[i];
      let placeholder = questionObject.placeholders[i];
      if (questionObject.textSpecs) {
        if (questionObject.textSpecs.prefix) {
          placeholder = questionObject.textSpecs.prefix + placeholder;
        }
        if (questionObject.textSpecs.suffix) {
          placeholder = placeholder + questionObject.textSpecs.suffix;
        }
      }
      // cl(contentKey, placeholder);
      inputsString += `
          <div class="input-wrapper">
            <input type="text" tabindex="${Constants.TABINDEX_CONTENT_GENERAL}" 
                data-contentKey="${ contentKey }" 
                placeholder="${ placeholder }">
          </div>`;
    }
    inputsString = `<div class="inputs">` + inputsString + `</div>`;

    // Make content.
    const s = `\
        <div class="prompt">${ questionObject.question }</div>
        ${ inputsString }`;
    const $els = $(s);      
    this.$content.append($els);

    this.#$textInputs = this.$el.find("input"); 
    this.#$textInputs.attr("maxLength", this.#charLength);

    // Add 'next' button
    const nextString = `<div class='cta-button' tabindex="${Constants.TABINDEX_CONTENT_END - 1}" >${Strings.get("lesson_accessibility_button_next")}</div>`;
    this.#$nextButton = $(nextString);
    this.$content.append(this.#$nextButton);
    FocusUtil.doStandardTreatment(this.#$nextButton);

    // Populate the inputs if pre-existing answer data exists
    if (answerObject) {
      if (answerObject.length >= 1) {
        this.#$textInputs[0].value = answerObject[0];
      }
      if (answerObject.length >= 2 && this.#$textInputs.length >= 2) {
        this.#$textInputs[1].value = answerObject[1];
      }
    }

    this.#isEnabled = false;
  }

  /**
   *  Override
   *  Gets called when view starts animate-in.
   */
  onShow() {
    super.onShow();
    // Wait before enabling and focusing into input.
    this.#isEnabled = false;
    setTimeout(() => {
      this.#isEnabled = true;
      this.#$textInputs[0].focus();
    }, 666);
  }

  set #isEnabled(b) {
    if (b) {
      this.#$textInputs.prop("disabled", false);
      this.#$textInputs.on("keydown", this.#onInputKeyDown);
      this.#$textInputs.on("keyup", this.#onInputKeyUp);
      this.#$nextButton.on('click tap', this.#onNextButton);
    } else {
      this.#$textInputs.prop("disabled", true);
      this.#$textInputs.off("keydown", this.#onInputKeyDown);
      this.#$textInputs.off("keyup", this.#onInputKeyUp);
      this.#$nextButton.off('click tap', this.#onNextButton);
    }
  }

  #processEnter(e) {

    const index = this.#getInputIndex(e.target);
    const b = this.#processInput(index);
    if (!b) {
      return;
    }
    if (this.#$textInputs.length === 1) {
      this.#sendEvent();
      return;
    } 

    // There are two inputs.
    const otherIndex = (index === 0) ? 1 : 0;
    this.#massageInput(otherIndex);

    const otherInput = this.#$textInputs[otherIndex];
    if (otherInput.value.length === 0) {
      this.#$textInputs[otherIndex].focus();
      return;
    }
    const otherOk = this.#processInput(otherIndex);
    if (!otherOk) {
      return;
    }
    this.#sendEvent();
  }

  #sendEvent() {
    const content = [];
    for (let i = 0; i < this.#$textInputs.length; i++) {
      const input = this.#$textInputs[i];
      let userText = input.value;
      userText = AppUtil.sanitizeUserInput(userText);
      userText = userText.trim();
      content.push(userText);
    }
    const o = {
      index: this.index,
      content: content
    };
    this.#isEnabled = false; // TODO: This should ideally go in a new super function ItemContentView.onHide()
    this.$el.trigger('content-view', o);
  }

  #getInputIndex(input) {
    for (let i = 0; i < this.#$textInputs.length; i++) {
      if (this.#$textInputs[i] == input) {
        return i;
      }
    }
    return -1;
  }

  #isInputValid(index) {

    const s = this.#$textInputs[index].value.trim();
    if (s.length === 0) {
      // Any blank text is ok.
      return true;
    }

    if (!this.#isInt && !this.#isFloat) {
      // Anything is permitted.
      return true;
    } else {
      const f = parseFloat(s);
      if (isNaN(f)) {
        // Is not a valid number.
        return false;
      }
      if (!this.#range) {
        // Is a valid number and there is no specified range, so let it go.
        return true;
      } 
      // Is okay if is in range.
      return (f >= this.#range[0] && f <= this.#range[1]);
    }
  }

  /** Returns false if invalid. */
  #processInput(index) {

    this.#massageInput(index);

    // Use the data placeholder text if empty.
    const input = this.#$textInputs[index];
    if (input.value.length === 0) {
      input.value = this.questionObject.placeholders[index];
    }

    // Validate.
    const b = this.#isInputValid(index);
    if (!b) {
      // Add error class, which will also trigger animation.
      const $input = $(input);
      if ($input.hasClass("error")) {
        $input.removeClass("error");
        // Must wait for a tick or so before re-applying error class to make @keyframes re-play.
        setTimeout(() => { $input.addClass("error"); }, 33);
      } else {
        $input.addClass("error");
      }

    } else {

      $(input).removeClass("error");
    }

    return b;
  }

  #massageInput(index) {
    const input = this.#$textInputs[index];
    let value = input.value;
    value = value.trim();
    if (this.#isInt) {
      const n = parseInt(value);
      // If it didn't work, leave as-is.
      value = isNaN(n) ? value : n;
    } else if (this.#isFloat) {
      const n = parseFloat(value);
      value = isNaN(n) ? value : n;
    }
    input.value = value;    
  }

  #onInputKeyDown = (e) => {
    if (!this.#isInt && !this.#isFloat) {
      return true;
    }
    //cl('[' + e.key + ']')
    if (e.key.length > 1) {
      // Is a special key so let it go.
      return true;
    }

    const b = this.#isInt
        ? /^\d*$/.test(e.key)  // digit only
        : /^\d*\.?\d*$/.test(e.key);  // digit or dot
    return b;
  };

  #onInputKeyUp = (e) => {
    const $focusedElement = $(':focus');
    if (e.keyCode === 13) {
      this.#processEnter(e);
    }
  };

  #onNextButton = (e) => {
    let isValid = true;
    for (let i = 0; i < this.#$textInputs.length; i++) {
      const b = this.#processInput(i);
      isValid = isValid && b;
    }
    if (isValid) {
      this.#sendEvent();
    }
  };
}
